// Manages drag-to-resize behavior for scheduled tasks.
import { useState, useEffect, useRef, useCallback } from 'react';
import { TIME_SLOT_MINS } from '@/config/constants';

/**
 * Custom hook to manage task resizing in the CalendarView.
 *
 * **Architectural Role:** This is a UX-critical component that converts a mouse drag
 * action (pixels) into a concrete task duration (minutes) for the database.
 *
 * **Pixel-to-Minute Ratio:** The ratio is fully determined by the `fifteenMinuteSlotHeight` prop.
 * 1 `fifteenMinuteSlotHeight` = exactly 15 minutes of task duration.
 *
 * **Snap-to-Grid Logic:** During a `mousemove` event, the delta Y in pixels is added to the initial
 * height. This new raw height is then rounded to the nearest multiple of `fifteenMinuteSlotHeight`
 * using `Math.round()`.
 * - Dragging < 7.5 minutes worth of pixels snaps down to the previous interval.
 * - Dragging >= 7.5 minutes worth of pixels snaps up to the next 15-minute interval.
 *
 * @param {Object} props
 * @param {Object} props.task - The task being resized (requires `id`).
 * @param {Object} props.style - The initial inline styles (reads `height`).
 * @param {number} props.fifteenMinuteSlotHeight - Pixel height of a 15-minute block.
 * @param {Function} props.onResizeComplete - Callback `(taskId, newDurationMins)` fired on mouseup.
 * @param {boolean} [props.disabled=false] - Whether resizing is disabled (e.g., read-only mode).
 * @returns {{ isResizing: boolean, currentHeight: number, handleMouseDown: Function }}
 */
const useTaskResize = ({
  task,
  style,
  fifteenMinuteSlotHeight,
  onResizeComplete,
  disabled = false,
}) => {
  const [isResizing, setIsResizing] = useState(false);

  // Helper function to ensure minimum height
  const ensureMinimumHeight = useCallback(
    (height) => {
      return Math.max(height, fifteenMinuteSlotHeight);
    },
    [fifteenMinuteSlotHeight],
  );

  // Initialize height from style, ensure it's not less than a single slot, fallback to a single slot height.
  const [currentHeight, setCurrentHeight] = useState(() => {
    const initialHeight = style?.height
      ? parseFloat(style.height)
      : fifteenMinuteSlotHeight;
    return ensureMinimumHeight(initialHeight);
  });

  const initialHeightRef = useRef(currentHeight);
  const initialMouseY = useRef(0);

  // Effect to synchronize the component's height from props.
  useEffect(() => {
    const newHeight = style?.height
      ? parseFloat(style.height)
      : fifteenMinuteSlotHeight;
    // Only update the height from props if the user is not actively resizing.
    if (!isResizing) {
      // Ensure the new height from props also respects the minimum height.
      setCurrentHeight(ensureMinimumHeight(newHeight));
    }
  }, [style?.height, fifteenMinuteSlotHeight, isResizing, ensureMinimumHeight]);

  /** @type {any} */
  const handleGlobalMouseMove = useCallback(
    (e) => {
      if (
        disabled ||
        typeof fifteenMinuteSlotHeight !== 'number' ||
        fifteenMinuteSlotHeight <= 0
      )
        return;
      const mouseEvent = /** @type {MouseEvent} */ (e);

      const deltaY = mouseEvent.clientY - initialMouseY.current;
      const newHeight = initialHeightRef.current + deltaY;

      // Snap the new height to the nearest 15-minute interval.
      const snappedHeight =
        Math.round(newHeight / fifteenMinuteSlotHeight) *
        fifteenMinuteSlotHeight;

      // The logic to ensure minimum height was already here and correct.
      setCurrentHeight(ensureMinimumHeight(snappedHeight));
    },
    [fifteenMinuteSlotHeight, disabled, ensureMinimumHeight],
  );

  /** @type {any} */
  const handleGlobalMouseUp = useCallback(() => {
    if (
      disabled ||
      typeof fifteenMinuteSlotHeight !== 'number' ||
      fifteenMinuteSlotHeight <= 0
    ) {
      setIsResizing(false);
      return;
    }

    // Calculate the new duration, ensuring it's at least TIME_SLOT_MINS minutes.
    const newDuration = Math.round(
      (currentHeight / fifteenMinuteSlotHeight) * TIME_SLOT_MINS,
    );

    // The duration calculation should also guarantee a minimum of TIME_SLOT_MINS.
    if (onResizeComplete) {
      onResizeComplete(task.id, Math.max(TIME_SLOT_MINS, newDuration));
    }

    setIsResizing(false);
  }, [
    currentHeight,
    fifteenMinuteSlotHeight,
    onResizeComplete,
    task.id,
    disabled,
  ]);

  // Effect to add or remove global event listeners based on the resizing state.
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isResizing, handleGlobalMouseMove, handleGlobalMouseUp]);

  // Mouse down handler for the resize handle.
  const handleMouseDown = (e) => {
    if (disabled) return;

    e.preventDefault();
    e.stopPropagation();

    setIsResizing(true);
    initialMouseY.current = e.clientY;
    initialHeightRef.current = currentHeight;
  };

  return {
    isResizing,
    currentHeight,
    handleMouseDown,
  };
};

export default useTaskResize;
